import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../app/store';
import { setRegionColor, setActiveRegionId, resetRegionColors, setActiveLayerId } from '../../../reducers/editorSlice';
import { CustomColorPicker } from "./CustomColorPicker";
import ColorLensIcon from '@mui/icons-material/ColorLens';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ColorizeIcon from '@mui/icons-material/Colorize';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Slider from '@mui/material/Slider';
import { toast } from 'react-toastify';
import LayersList from '../layers-list';

interface Props {
  setActiveOverlay?: (overlay: string) => void;
}

const BLEND_OPTIONS = [
  { label: 'Normal', value: 'source-over' },
  { label: 'Multiply', value: 'multiply' },
  { label: 'Screen', value: 'screen' },
  { label: 'Overlay', value: 'overlay' },
  { label: 'Darken', value: 'darken' },
  { label: 'Lighten', value: 'lighten' },
  { label: 'Color Dodge', value: 'color-dodge' },
  { label: 'Color Burn', value: 'color-burn' },
  { label: 'Soft Light', value: 'soft-light' },
  { label: 'Hard Light', value: 'hard-light' },
  { label: 'Difference', value: 'difference' },
  { label: 'Exclusion', value: 'exclusion' },
];

const ColorSettingsSidebar: React.FC<Props> = ({ setActiveOverlay }) => {
  const dispatch = useDispatch();
  const { regions, regionColors, activeRegionId, activeLayerId, selectedObjectProps, currentDesign } = useSelector((state: RootState) => state.editor);
  const hasGeneratedDesign = Boolean(currentDesign?.design_id);

  const activeRegion = regions.find(r => r.id === activeRegionId);
  const currentColor = activeRegionId && regionColors[activeRegionId] ? regionColors[activeRegionId] : (activeRegion?.originalColor || "#2ECC71");
  const [hexDraft, setHexDraft] = useState(currentColor.toUpperCase());
  const [blendMenuOpen, setBlendMenuOpen] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const blendMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onHistoryChanged = (e: Event) => {
      const detail = (e as CustomEvent<{ canUndo?: boolean; canRedo?: boolean }>).detail || {};
      setCanUndo(!!detail.canUndo);
      setCanRedo(!!detail.canRedo);
    };
    window.addEventListener('editor-history-changed', onHistoryChanged);
    window.dispatchEvent(new CustomEvent('editor-history-request'));
    return () => window.removeEventListener('editor-history-changed', onHistoryChanged);
  }, []);

  const handleUndo = () => {
    window.dispatchEvent(new CustomEvent('editor-undo'));
  };

  const handleRedo = () => {
    window.dispatchEvent(new CustomEvent('editor-redo'));
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (blendMenuRef.current && !blendMenuRef.current.contains(event.target as Node)) {
        setBlendMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setHexDraft(currentColor.toUpperCase());
  }, [currentColor, activeRegionId]);

  const handleColorChange = (newColor: string) => {
    if (!activeRegionId) return;
    const previous = (regionColors[activeRegionId] || activeRegion?.originalColor || '').toLowerCase();
    if (previous === newColor.toLowerCase()) return;
    dispatch(setRegionColor({ id: activeRegionId, color: newColor }));
  };

  const commitHex = (raw: string) => {
    const hex = raw.trim().startsWith('#') ? raw.trim() : `#${raw.trim()}`;
    if (!/^#([0-9A-Fa-f]{6})$/.test(hex)) {
      setHexDraft(currentColor.toUpperCase());
      return;
    }
    handleColorChange(hex);
  };

  const handleEyedropper = async () => {
    const EyeDropperCtor = (window as any).EyeDropper;
    if (!EyeDropperCtor) {
      toast.info('Eyedropper is not supported in this browser. Use the color wheel.');
      return;
    }
    try {
      const result = await new EyeDropperCtor().open();
      if (result?.sRGBHex) handleColorChange(result.sRGBHex);
    } catch {
      // user cancelled
    }
  };

  return (
    <>
      <div className="editor_card">
        <h3>Overlays</h3>
        <div className="overlay_tabs">
          <figure
            className="tab_btn"
            role="button"
            tabIndex={0}
            onClick={() => setActiveOverlay?.('text')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setActiveOverlay?.('text');
              }
            }}
          >
            <img src="/static/images/text_icon.svg" alt="text_icon" />
            <figcaption>Text</figcaption>
          </figure>
          <figure className="tab_btn active">
            <ColorLensIcon sx={{ fontSize: 20 }} />
            <figcaption>Color</figcaption>
          </figure>
          <figure
            className="tab_btn"
            role="button"
            tabIndex={0}
            onClick={() => setActiveOverlay?.('upload')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setActiveOverlay?.('upload');
              }
            }}
          >
            <img src="/static/images/upload.svg" alt="color_upload_icon" />
            <figcaption>Upload</figcaption>
          </figure>
        </div>
      </div>

      {hasGeneratedDesign && (
        <div className="editor_card color_customization">
          <div className="color_customization_header">
            <h3>Color Customization</h3>
            <div className="color_header_actions">
              <button
                type="button"
                className="color_history_btn"
                title="Undo (Ctrl+Z)"
                aria-label="Undo"
                onClick={handleUndo}
                disabled={!canUndo}
              >
                <img src="/static/images/undo.svg" alt="" />
                <span>Undo</span>
              </button>
              <button
                type="button"
                className="color_history_btn"
                title="Redo (Ctrl+Y)"
                aria-label="Redo"
                onClick={handleRedo}
                disabled={!canRedo}
              >
                <img src="/static/images/redo.svg" alt="" />
                <span>Redo</span>
              </button>
              {regions.length > 0 && (
                <button
                  type="button"
                  onClick={() => dispatch(resetRegionColors())}
                  className="color_reset_btn"
                  title="Reset all colors"
                >
                  Reset
                </button>
              )}
              <button
                type="button"
                className="eyedropper_btn"
                title="Pick a color from the screen"
                onClick={handleEyedropper}
                disabled={!activeRegion}
              >
                <ColorizeIcon sx={{ fontSize: 18 }} />
              </button>
            </div>
          </div>

          <div className="region_picker">
            <span className="region_label">Choose a part of the design</span>
            <p className="region_hint">Select a region first. The color wheel below will recolor only that part of the image.</p>
            {regions.length === 0 ? (
              <div className="region_empty">Generate a design to recolor its parts.</div>
            ) : (
              <div className="region_list" role="listbox" aria-label="Design regions">
                {regions.map((region) => {
                  const color = regionColors[region.id] || region.originalColor;
                  const selected = activeRegionId === region.id;
                  return (
                    <div
                      key={region.id}
                      className={`region_row ${selected ? 'active' : ''}`}
                    >
                      <button
                        type="button"
                        role="option"
                        aria-selected={selected}
                        className={`region_chip ${selected ? 'active' : ''}`}
                        onClick={() => {
                          dispatch(setActiveRegionId(region.id));
                          dispatch(setActiveLayerId(region.id));
                        }}
                      >
                        <span className="color_dot" style={{ backgroundColor: color }} />
                        <span className="region_chip_name">{region.name}</span>
                        {selected && <span className="region_chip_tag">Editing</span>}
                      </button>
                      <button
                        type="button"
                        className="region_delete_btn"
                        title={`Delete ${region.name}`}
                        aria-label={`Delete ${region.name}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          window.dispatchEvent(
                            new CustomEvent('remove-fabric-object', { detail: { layerId: region.id } })
                          );
                        }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {activeRegion && (
            <>
              <div className="active_region_header">
                <p className="picker_hint">Now pick a color for <strong>{activeRegion.name}</strong></p>
                <button
                  type="button"
                  className="delete_selected_region_btn"
                  title={`Delete ${activeRegion.name}`}
                  aria-label={`Delete ${activeRegion.name}`}
                  onClick={() => {
                    window.dispatchEvent(
                      new CustomEvent('remove-fabric-object', { detail: { layerId: activeRegion.id } })
                    );
                  }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                  <span>Delete {activeRegion.name}</span>
                </button>
              </div>
              <CustomColorPicker color={currentColor} onChange={handleColorChange} />
              <div className="hex_input">
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div className="color_preview" style={{ background: currentColor, borderRadius: '4px' }}></div>
                  <span>HEX</span>
                </div>
                <input
                  className="hex_field"
                  value={hexDraft}
                  onChange={(e) => setHexDraft(e.target.value.toUpperCase())}
                  onBlur={() => commitHex(hexDraft)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitHex(hexDraft);
                  }}
                  spellCheck={false}
                  aria-label="Hex color"
                />
                <ContentCopyIcon
                  className="copy_icon"
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    navigator.clipboard.writeText(currentColor.toUpperCase());
                    toast.success('Color copied');
                  }}
                />
              </div>
            </>
          )}
        </div>
      )}

      <div className="editor_card layers_card">
        <h3>Layers</h3>
        <LayersList />
      </div>

      <div className="editor_card layer_settings">
        <h3>Layer Setting</h3>
        <div className="setting_row">
          <div className="header">
            <span>Opacity</span>
            <span className="value">{Math.round((selectedObjectProps?.opacity ?? 1) * 100)}%</span>
          </div>
          <Slider
            className="progress_bar"
            value={Math.round((selectedObjectProps?.opacity ?? 1) * 100)}
            min={0}
            max={100}
            disabled={!activeLayerId}
            onChange={(_e, val) => {
              if (!activeLayerId) return;
              window.dispatchEvent(new CustomEvent('update-fabric-props', {
                detail: { layerId: activeLayerId, props: { opacity: (val as number) / 100 } },
              }));
            }}
          />
        </div>

        <div className="setting_row">
          <div className="header">
            <span>Blend Mode</span>
          </div>
          <div className={`dropdown blend_dropdown ${blendMenuOpen ? 'is_open' : ''}`} ref={blendMenuRef}>
            <button
              type="button"
              className={`blend_trigger ${blendMenuOpen ? 'open' : ''}`}
              disabled={!activeLayerId}
              onClick={() => setBlendMenuOpen((prev) => !prev)}>
              <span>
                {BLEND_OPTIONS.find((opt) => opt.value === (selectedObjectProps?.blendMode || 'source-over'))?.label || 'Normal'}
              </span>
              <KeyboardArrowDownIcon className="arrow" />
            </button>
            {blendMenuOpen && (
              <div className="blend_dropdown_menu" role="listbox">
                {BLEND_OPTIONS.map((opt) => {
                  const isActive = (selectedObjectProps?.blendMode || 'source-over') === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="option"
                      aria-selected={isActive}
                      className={`blend_dropdown_option ${isActive ? 'active' : ''}`}
                      onClick={() => {
                        if (!activeLayerId) return;
                        window.dispatchEvent(
                          new CustomEvent('update-fabric-props', {
                            detail: { layerId: activeLayerId, props: { blendMode: opt.value } },
                          })
                        );
                        setBlendMenuOpen(false);
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ColorSettingsSidebar;
